import { Component, inject } from '@angular/core';
import { BackendApi } from '../../services/backend-api';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AgGridAngular } from "ag-grid-angular";
import type { AutoSizeStrategy, ColDef, GetRowIdParams, GridApi, GridReadyEvent, ValueFormatterParams } from "ag-grid-community";
import { Package } from '../../models/package';
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { MatButtonModule } from '@angular/material/button';
import { CreatePackageDialog } from '../create-package-dialog/create-package-dialog';
import { MatDialog } from '@angular/material/dialog';
import { IconButtonCellRenderer } from '../icon-button-cell-renderer/icon-button-cell-renderer';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation-dialog';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { UpdatePackageDialog } from '../update-package-dialog/update-package-dialog';

@Component({
  selector: 'app-list-packages',
  imports: [AgGridAngular, MatButtonModule, MatIconModule],
  templateUrl: './list-packages.html',
  styleUrl: './list-packages.css',
})
export class ListPackages {
  constructor(
    private readonly backendApi: BackendApi,
    private readonly dialog: MatDialog
  ) { }

  private _snackBar = inject(MatSnackBar);
  private _router = inject(Router);

  public autoSizeStrategy: AutoSizeStrategy = {
    type: 'fitGridWidth',
    defaultMinWidth: 100,
  };

  public columns: ColDef<Package>[] = [
    { field: "name", headerName: "Nom", filter: true, floatingFilter: true },
    { field: "description", headerName: "Description", filter: false, sortable: false },
    { field: "type", headerName: "Type", valueFormatter: params => {
      switch(params.value) {
        case 'monthly':
          return "Mensuel";
        case 'unlimited':
          return "Illimité";
        default:
          return "";
      }
    }  },
    { field: "limit", headerName: "Limite de conso. (heures)", filter: false, floatingFilter: false, valueFormatter: params => {
      if (params?.data?.type != "monthly") {
        return "";
      }

      return (params.value / 60 / 60).toFixed(2);
    } },
    { field: "priority_level", headerName: "Priorité", filter: false, floatingFilter: false },
    {
      cellRenderer: IconButtonCellRenderer,
      cellRendererParams: {
        icon: 'edit',
        tooltip: 'Modifier ce forfait',
        onClick: (data: any, index: number) => this.updatePackage(data, index)
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      width: 50
    },
    {
      cellRenderer: IconButtonCellRenderer,
      cellRendererParams: {
        icon: 'delete',
        tooltip: 'Supprimer ce forfait',
        onClick: (data: any, index: number) => this.deletePackage(data, index)
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      width: 50
    }
  ];

  public localeText = AG_GRID_LOCALE_FR;

  private gridApi!: GridApi;
  

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;

    this.backendApi.getPackages().subscribe({
      next: (packages) => {
        event.api.setGridOption('rowData', packages);
        event.api.setGridOption('loading', false);
      },
      error: () => {
        // Error encountered
        event.api.setGridOption('loading', false);
        this._snackBar.open("Erreur rencontrée lors de la recuperation des forfaits.");
      }
    })
  }

  getRowId(params: GetRowIdParams): string {
    return params.data.uuid;
  }

  selectPackage(uuid?: string) {
    if (!uuid) {
      return;
    }

    this._router.navigate(["/packages", uuid]);
  }

  createPackage() {
    const dialogRef = this.dialog.open(CreatePackageDialog, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe((newPackage) => {
      if (newPackage) {
        this.gridApi.applyTransaction({
          add: [newPackage]
        });
      }
    });
  }

  updatePackage(thePackage: any, index: number): void {
    const dialogRef = this.dialog.open(UpdatePackageDialog, {
      width: '400px',
      data: thePackage
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.gridApi.applyTransaction({update: [result]});
    });
  }

  deletePackage(thePackage: any, index: number): void {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {content: "Êtes-vous sûr de vouloir supprimer ce forfait ?"},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.backendApi.deletePackage(thePackage.uuid).subscribe({
        next: () => {
          this.gridApi.applyTransaction({remove: [thePackage]});
          this._snackBar.open("Forfait supprimé.");
        },
        error: () => {
          this._snackBar.open("Erreur rencontrée lors de la suppression.");
        }
      })
    });
  }
}
